(() => {
  if (customElements.get('maxton-vehicle-picker')) return;

  // Constants
  const MIN_COLUMNS = 5;
  const SEARCH_RESULT_LIMIT = 8;
  const GRID_COLUMN_DEPTH = 2;
  const DEPTH_TYPES = ['brand', 'model', 'version', 'generation', 'bodytype'];
  const PATH_SEPARATOR = ' › ';
  const SCROLL_EDGE_THRESHOLD = 4;
  const VEHICLE_PARAM = 'vehicle';
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

  // Generic helpers
  const createElement = (tagName, { className, text, attributes = {} } = {}, children = []) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    Object.entries(attributes).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== false) element.setAttribute(name, value === true ? '' : value);
    });
    element.append(...children.filter(Boolean));
    return element;
  };

  const normalizeText = (value = '') =>
    String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const compareLabels = (left, right) => left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: 'base' });

  // Priority brands first (in the configured order), then the rest alphabetically
  const buildBrandRank = (priorityBrands = []) => {
    const priorityLabels = priorityBrands.map(normalizeText).filter(Boolean);
    return ({ label }) => {
      const normalizedLabel = normalizeText(label);
      const index = priorityLabels.findIndex((priority) => priority === normalizedLabel || priority.startsWith(`${normalizedLabel} `));
      return index === -1 ? Number.POSITIVE_INFINITY : index;
    };
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const script = createElement('script', { attributes: { src, async: true } });
      script.addEventListener('load', resolve);
      script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      document.head.append(script);
    });

  // Vehicle taxonomy (same data source as the chat bot)
  const loadVehicleTaxonomy = async (loaderUrl) => {
    if (!window.MaxtonVehicleDataPromise) await loadScript(loaderUrl);
    if (!window.MaxtonVehicleDataPromise) throw new Error('Vehicle taxonomy loader did not start');
    return window.MaxtonVehicleDataPromise;
  };

  const buildVehicleTree = ({ rootId, nodes }, { priorityBrands = [] } = {}) => {
    const nodeById = new Map(nodes.map((node) => [Number(node.id), node]));
    const childrenByParent = new Map();
    nodes.forEach((node) => {
      const parentId = Number(node.parentId);
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(node);
    });

    const getBrandRank = buildBrandRank(priorityBrands);
    childrenByParent.forEach((children, parentId) => {
      children.sort(
        parentId === Number(rootId) ? (left, right) => getBrandRank(left) - getBrandRank(right) || compareLabels(left, right) : compareLabels,
      );
    });

    const getPath = (nodeId) => {
      const path = [];
      for (let node = nodeById.get(Number(nodeId)); node; node = nodeById.get(Number(node.parentId))) path.unshift(node);
      return path;
    };

    const searchEntries = nodes.map((node) => {
      const path = getPath(node.id);
      return { node, depth: path.length, searchText: normalizeText(path.map(({ label }) => label).join(' ')) };
    });

    return {
      rootId: Number(rootId),
      getPath,
      getChildren: (nodeId) => childrenByParent.get(Number(nodeId)) ?? [],
      isLeaf: (node) => !childrenByParent.has(Number(node.id)),
      isPriorityBrand: (node) => Number.isFinite(getBrandRank(node)),
      search: (query) => {
        const tokens = normalizeText(query).split(' ').filter(Boolean);
        if (!tokens.length) return [];
        return searchEntries
          .filter(({ searchText }) => tokens.every((token) => searchText.includes(token)))
          .sort((left, right) => left.depth - right.depth)
          .slice(0, SEARCH_RESULT_LIMIT)
          .map(({ node }) => node);
      },
    };
  };

  // Vehicle picker element
  class MaxtonVehiclePicker extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      this.config = JSON.parse(this.querySelector('[data-vehicle-picker-config]').textContent);
      this.elements = {
        search: this.querySelector('[data-vehicle-picker-search]'),
        results: this.querySelector('[data-vehicle-picker-results]'),
        columns: this.querySelector('[data-vehicle-picker-columns]'),
        status: this.querySelector('[data-vehicle-picker-status]'),
      };
      this.state = { path: [], expandedDepth: null };
      this.mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);

      this.mobileMedia.addEventListener('change', () => this.tree && this.renderColumns());
      this.elements.search.addEventListener('input', () => this.renderSearchResults());
      this.elements.search.addEventListener('keydown', (event) => this.handleSearchKeydown(event));
      this.loadTree();
    }

    // Data loading
    async loadTree() {
      const { texts } = this.config;
      this.elements.status.textContent = texts.loading;
      try {
        this.tree = buildVehicleTree(await loadVehicleTaxonomy(this.config.loaderUrl), { priorityBrands: this.config.priorityBrands });
        this.elements.status.textContent = '';
        this.state.path = this.getInitialPath();
        this.renderColumns();
      } catch (error) {
        console.warn('[vehicle-picker] taxonomy unavailable', error);
        this.elements.status.textContent = texts.error;
      }
    }

    // ?vehicle= (redirect from a non-final car collection): start with its branch open
    getInitialPath() {
      const vehicleId = new URLSearchParams(window.location.search).get(VEHICLE_PARAM);
      return vehicleId ? this.tree.getPath(vehicleId).filter((node) => !this.tree.isLeaf(node)) : [];
    }

    // Navigation
    buildBuilderUrl(node) {
      const url = new URL(node.link, window.location.origin);
      url.searchParams.set('view', this.config.builderView);
      return `${url.pathname}${url.search}`;
    }

    buildVehicleTitle(node) {
      const path = this.tree.getPath(node.id);
      const titleNodes = path.length > GRID_COLUMN_DEPTH ? [path[0], ...path.slice(GRID_COLUMN_DEPTH)] : path;
      return titleNodes.map(({ label }) => label).join(' ');
    }

    selectNode(depth, node) {
      this.state.path = [...this.state.path.slice(0, depth - 1), node];
      this.state.expandedDepth = null;
      this.renderColumns();

      // Mobile: the chosen step collapses in place, so keep its header at the top and the next step right below it
      if (this.isMobile()) this.columnElements[depth - 1]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }

    // Mobile step collapse
    isMobile() {
      return this.mobileMedia.matches;
    }

    toggleColumn(depth) {
      this.state.expandedDepth = this.state.expandedDepth === depth ? null : depth;
      this.renderColumns();
    }

    openNode(node) {
      this.state.path = this.tree.getPath(node.id);
      this.state.expandedDepth = null;
      this.elements.search.value = '';
      this.renderSearchResults();
      this.renderColumns();
      this.elements.columns.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }

    // Search
    handleSearchKeydown(event) {
      if (event.key === 'Escape') {
        this.elements.search.value = '';
        this.renderSearchResults();
        return;
      }
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.elements.results.querySelector('a, button')?.click();
    }

    renderSearchResults() {
      const { results, search } = this.elements;
      const query = search.value.trim();
      results.hidden = !query || !this.tree;
      if (results.hidden) return results.replaceChildren();

      const nodes = this.tree.search(query);
      results.replaceChildren(
        ...(nodes.length
          ? nodes.map((node) => createElement('li', {}, [this.renderSearchResult(node)]))
          : [createElement('li', { className: 'vehicle-picker__no-results', text: this.config.texts.noResults })]),
      );
    }

    renderSearchResult(node) {
      const label = this.tree.getPath(node.id).map(({ label: pathLabel }) => pathLabel).join(PATH_SEPARATOR);
      if (this.tree.isLeaf(node)) {
        return createElement('a', { className: 'vehicle-picker__result', attributes: { href: this.buildBuilderUrl(node) } }, [
          createElement('span', { text: label }),
          createElement('span', { className: 'vehicle-picker__result-action', text: `${this.config.texts.configure} →` }),
        ]);
      }

      const button = createElement('button', { className: 'vehicle-picker__result', attributes: { type: 'button' } }, [
        createElement('span', { text: label }),
      ]);
      button.addEventListener('click', () => this.openNode(node));
      return button;
    }

    // Columns
    getColumns() {
      const parents = [{ id: this.tree.rootId }, ...this.state.path];
      const columns = parents
        .map((parent, index) => ({ depth: index + 1, parentId: parent.id, nodes: this.tree.getChildren(parent.id) }))
        .filter(({ nodes }) => nodes.length);
      const placeholders = Array.from({ length: Math.max(MIN_COLUMNS - columns.length, 0) }, (_, index) => ({
        depth: columns.length + index + 1,
        parentId: null,
        nodes: [],
      }));
      return [...columns, ...placeholders];
    }

    // Final column: the one with cars to configure, otherwise the next empty step; columns after it are not reachable yet
    getFinalColumnIndex(columns) {
      const leafColumnIndex = columns.findIndex(({ nodes }) => nodes.length && nodes.every((node) => this.tree.isLeaf(node)));
      if (leafColumnIndex !== -1) return leafColumnIndex;
      const nextEmptyColumnIndex = columns.findIndex(({ nodes }) => !nodes.length);
      return nextEmptyColumnIndex === -1 ? columns.length - 1 : nextEmptyColumnIndex;
    }

    // Column elements are reused between renders so color changes can animate
    renderColumns() {
      const columns = this.getColumns();
      const finalIndex = this.getFinalColumnIndex(columns);
      const previousElements = this.columnElements ?? [];

      this.columnElements = columns.map((column, index) =>
        this.updateColumnElement(previousElements[index] ?? createElement('section'), column, {
          isFinal: index === finalIndex,
          isDisabled: index > finalIndex,
        }),
      );
      previousElements.slice(columns.length).forEach((element) => element.remove());
      this.elements.columns.append(...this.columnElements.filter((element) => !element.isConnected));
    }

    updateColumnElement(element, column, columnState) {
      const { isFinal, isDisabled } = columnState;
      const isCollapsible = Boolean(this.state.path[column.depth - 1]);
      const isCollapsed = isCollapsible && this.isMobile() && this.state.expandedDepth !== column.depth;

      element.className = [
        'vehicle-picker__column',
        isFinal && 'vehicle-picker__column--final',
        isDisabled && 'vehicle-picker__column--disabled',
        isCollapsible && 'vehicle-picker__column--collapsible',
        isCollapsed && 'vehicle-picker__column--collapsed',
      ]
        .filter(Boolean)
        .join(' ');

      const contentKey = [column.parentId, this.state.path[column.depth - 1]?.id, isFinal].join(':');
      if (element.dataset.contentKey !== contentKey) {
        const hasNewOptions = column.nodes.length > 0 && element.dataset.parentId !== String(column.parentId);
        element.replaceChildren(...this.renderColumnContent(column, columnState));
        element.dataset.contentKey = contentKey;
        element.dataset.parentId = String(column.parentId);
        if (hasNewOptions) element.querySelector('.vehicle-picker__options')?.classList.add('is-entering');
        this.revealSelectedOption(element);
      }

      element.querySelector('.vehicle-picker__column-toggle')?.setAttribute('aria-expanded', String(!isCollapsed));
      return element;
    }

    getColumnTitle({ depth, nodes }) {
      const type = nodes[0]?.type ?? DEPTH_TYPES[depth - 1] ?? DEPTH_TYPES.at(-1);
      return this.config.texts.columnTitles[type] ?? '';
    }

    renderColumnContent(column, { isFinal }) {
      const { depth, nodes } = column;
      const { texts } = this.config;
      const isBrandColumn = depth === 1;
      const listClassName = ['vehicle-picker__options', depth === GRID_COLUMN_DEPTH && 'vehicle-picker__options--grid'].filter(Boolean).join(' ');
      const optionsContent = isBrandColumn ? this.renderBrandGroups(nodes) : nodes.length > 0 && this.renderOptionList(nodes, depth, listClassName);
      const selectedNode = this.state.path[depth - 1];

      return [
        createElement('header', { className: 'vehicle-picker__column-header' }, [
          createElement('span', { className: 'vehicle-picker__column-number', text: String(depth) }),
          createElement('h2', { className: 'vehicle-picker__column-title', text: this.getColumnTitle(column) }),
          selectedNode && createElement('span', { className: 'vehicle-picker__column-value', text: selectedNode.label }),
          selectedNode && this.renderColumnToggle(depth),
        ]),
        // Collapsible wrapper (layout-neutral on desktop, see CSS)
        createElement('div', { className: 'vehicle-picker__column-content' }, [
          createElement('div', { className: 'vehicle-picker__column-content-inner' }, [
            isFinal && createElement('p', { className: 'vehicle-picker__column-hint', text: texts.finalHint }),
            isBrandColumn && nodes.length > 0 && this.renderListFilter(),
            optionsContent && this.renderColumnBody(optionsContent),
          ]),
        ]),
      ];
    }

    // Mobile: header-wide button that collapses / expands a completed step
    renderColumnToggle(depth) {
      const label = this.config.texts.changeSelection || 'Zmień wybór';
      const button = createElement('button', {
        className: 'vehicle-picker__column-toggle',
        attributes: { type: 'button', 'aria-label': label },
      });
      button.addEventListener('click', () => this.toggleColumn(depth));
      return button;
    }

    // Scrollable column body: fades show there is more content above or below
    renderColumnBody(content) {
      const body = createElement('div', { className: 'vehicle-picker__column-body' }, [content]);
      const updateScrollFades = () => {
        const { scrollTop, scrollHeight, clientHeight } = body;
        body.classList.toggle('has-more-above', scrollTop > SCROLL_EDGE_THRESHOLD);
        body.classList.toggle('has-more-below', scrollTop + clientHeight < scrollHeight - SCROLL_EDGE_THRESHOLD);
      };
      body.addEventListener('scroll', updateScrollFades, { passive: true });
      body.addEventListener('vehicle-picker:refresh', updateScrollFades);
      return body;
    }

    // Keeps the selected option in view after a column is rebuilt
    revealSelectedOption(columnElement) {
      const body = columnElement.querySelector('.vehicle-picker__column-body');
      if (!body) return;
      requestAnimationFrame(() => {
        const selected = body.querySelector('.is-selected');
        if (selected) body.scrollTop = Math.max(selected.offsetTop - body.clientHeight / 2 + selected.offsetHeight / 2, 0);
        body.dispatchEvent(new Event('vehicle-picker:refresh'));
      });
    }

    renderOptionList(nodes, depth, className = 'vehicle-picker__options') {
      const selectedNode = this.state.path[depth - 1];
      return createElement(
        'ul',
        { className },
        nodes.map((node) =>
          createElement('li', { attributes: { 'data-search-text': normalizeText(node.label) } }, [
            this.tree.isLeaf(node) ? this.renderVehicleCard(node) : this.renderOptionButton(node, depth, node === selectedNode),
          ]),
        ),
      );
    }

    // Brands: highlighted priority group, then all other brands A–Z
    renderBrandGroups(nodes) {
      const { texts } = this.config;
      const priorityNodes = nodes.filter((node) => this.tree.isPriorityBrand(node));
      const otherNodes = nodes.filter((node) => !this.tree.isPriorityBrand(node));

      return createElement('div', { className: 'vehicle-picker__brand-groups' }, [
        priorityNodes.length > 0 &&
          createElement('div', { className: 'vehicle-picker__brand-group vehicle-picker__brand-group--priority' }, [
            texts.priorityBrandsTitle && createElement('p', { className: 'vehicle-picker__brand-group-title', text: `★ ${texts.priorityBrandsTitle}` }),
            this.renderOptionList(priorityNodes, 1),
          ]),
        otherNodes.length > 0 &&
          createElement('div', { className: 'vehicle-picker__brand-group' }, [
            priorityNodes.length > 0 && texts.allBrandsTitle && createElement('p', { className: 'vehicle-picker__brand-group-title', text: texts.allBrandsTitle }),
            this.renderOptionList(otherNodes, 1),
          ]),
      ]);
    }

    renderListFilter() {
      const input = createElement('input', {
        className: 'vehicle-picker__filter',
        attributes: { type: 'search', placeholder: this.config.texts.brandFilterPlaceholder, 'aria-label': this.config.texts.brandFilterPlaceholder },
      });
      input.addEventListener('input', () => {
        const query = normalizeText(input.value);
        const column = input.closest('.vehicle-picker__column');
        column.querySelectorAll('.vehicle-picker__options li').forEach((item) => {
          item.hidden = Boolean(query) && !item.dataset.searchText.includes(query);
        });
        column.querySelectorAll('.vehicle-picker__brand-group').forEach((group) => {
          group.hidden = !group.querySelector('li:not([hidden])');
        });
        column.querySelector('.vehicle-picker__column-body')?.dispatchEvent(new Event('vehicle-picker:refresh'));
      });
      return input;
    }

    renderOptionButton(node, depth, isSelected) {
      const button = createElement('button', {
        className: `vehicle-picker__option${isSelected ? ' is-selected' : ''}`,
        text: node.label,
        attributes: { type: 'button', 'aria-pressed': String(isSelected) },
      });
      button.addEventListener('click', () => this.selectNode(depth, node));
      return button;
    }

    renderVehicleCard(node) {
      return createElement('a', { className: 'vehicle-picker__vehicle', attributes: { href: this.buildBuilderUrl(node) } }, [
        createElement('span', { className: 'vehicle-picker__vehicle-title', text: this.buildVehicleTitle(node) }),
        createElement('span', { className: 'vehicle-picker__vehicle-action', text: `${this.config.texts.configure} →` }),
      ]);
    }
  }

  customElements.define('maxton-vehicle-picker', MaxtonVehiclePicker);
})();